import { useCallback, useState } from 'react';
import api from '../services/api';

/**
 * @module useSendMonthlyReports
 * @description Custom hook for sending monthly reports in the Mental Health Portal application.
 * Manages loading and error states, and provides a memoized function for sending reports.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to send monthly reports
 * @function useSendMonthlyReports
 * @returns {Object} Object containing sendReports function, loading state, and error state
 */
export const useSendMonthlyReports = () => {
  /**
   * @state {boolean} loading - Indicates if the send operation is in progress
   */
  const [loading, setLoading] = useState(false);

  /**
   * @state {string|null} error - Error message if the send operation fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Sends all reviewed monthly reports
   * @async
   * @function sendReports
   * @returns {Promise<Object>} The response data from the API
   * @throws {Error} If the send operation fails
   */
  const sendReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post('send-monthly-report');
      return data;
    } catch (err) {
      setError('Erro ao enviar relatórios mensais.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * @typedef {Object} UseSendMonthlyReportsReturn
   * @property {Function} sendReports - Function to send monthly reports
   * @property {boolean} loading - Indicates if the send operation is in progress
   * @property {string|null} error - Error message if the send operation fails, null otherwise
   */

  /**
   * @returns {UseSendMonthlyReportsReturn} The hook's return values
   */
  return {
    sendReports,
    loading,
    error,
  };
};
